import { type CSSProperties, type ReactElement } from 'react';
import { cn } from '@/lib/cn';
import {
  DRONE_KEYS,
  DRONE_INTERVALS,
  DRONE_OCTAVES,
  type DroneKey,
  type DroneInterval,
  type DroneOctave,
  type DroneKeyInfo,
} from '@/lib/practice/drone-keys';

export type DroneSurface = 'light' | 'dark';

export type DroneControlProps = {
  surface: DroneSurface;
  root: DroneKey | null;
  interval: DroneInterval;
  octave: DroneOctave;
  volume: number;
  on: boolean;
  onRootChange: (root: DroneKey) => void;
  onIntervalChange: (interval: DroneInterval) => void;
  onOctaveChange: (octave: DroneOctave) => void;
  onVolumeChange: (volume: number) => void;
  onOnChange: (on: boolean) => void;
  onAutoMatch?: () => void;
  autoMatchDisabled?: boolean;
  compact?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
};

const WAVE_BAR_COUNT = 40;
const VOLUME_PERCENT = 100;

// The dark surface (mobile bottom-sheet over the dimmed scene) reuses the
// molecule's token-driven markup unchanged — it just remaps the light tokens to
// dark values locally, so a single style override drives the whole tree.
const DARK_SURFACE_TOKENS: CSSProperties = {
  '--bg-surface': '#1a1a1a',
  '--bg-page': '#141414',
  '--bg-soft': '#262626',
  '--bg-inset': '#303030',
  '--ink': '#f5f5f3',
  '--ink-2': '#c8c8c2',
  '--ink-3': '#8f8f88',
  '--ink-4': '#5c5c57',
  '--rule': '#333333',
} as CSSProperties;

function playingKeyInfo(root: DroneKey | null, on: boolean): DroneKeyInfo | null {
  if (!on || root === null) return null;
  return DRONE_KEYS.find((k) => k.note === root) ?? null;
}

function DroneWave({ playing }: { playing: boolean }): ReactElement {
  return (
    <div className="flex h-7 items-center justify-center gap-[2px]" aria-hidden="true">
      {Array.from({ length: WAVE_BAR_COUNT }).map((_, i) => {
        const height = playing ? (Math.sin(i * 0.6) * 0.4 + 0.6) * 100 : 14;
        const opacity = playing ? 0.4 + Math.abs(Math.sin(i * 0.4)) * 0.6 : 0.18;
        return (
          <div
            key={i}
            className="w-[2px] rounded-full bg-[var(--ink)] transition-all duration-300"
            style={{ height: `${height}%`, opacity }}
          />
        );
      })}
    </div>
  );
}

export function DroneControl({
  surface,
  root,
  interval,
  octave,
  volume,
  on,
  onRootChange,
  onIntervalChange,
  onOctaveChange,
  onVolumeChange,
  onOnChange,
  onAutoMatch,
  autoMatchDisabled = false,
  compact = false,
  showHeader = true,
  showFooter = true,
}: DroneControlProps): ReactElement {
  const playing = playingKeyInfo(root, on);
  const surfaceTokens = surface === 'dark' ? DARK_SURFACE_TOKENS : undefined;

  const idleButton =
    'bg-[var(--bg-soft)] text-[var(--ink-2)] hover:bg-[var(--bg-inset)]';
  const activeButton = 'bg-[var(--ink)] text-white shadow-sm';

  return (
    <div className="flex flex-col bg-[var(--bg-surface)] text-[var(--ink)]" style={surfaceTokens}>
      {showHeader && (
        <div
          className={cn(
            'flex items-center justify-between border-b border-[var(--rule)] px-5',
            compact ? 'h-12' : 'h-14',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cn(
                'h-2 w-2 rounded-full',
                playing ? 'animate-pulse bg-[var(--warm)]' : 'bg-[var(--ink-4)]',
              )}
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
              Drone {playing ? '· playing' : '· off'}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="Drone"
            onClick={() => onOnChange(!on)}
            className={cn(
              'h-6 w-10 rounded-full p-0.5 transition-colors',
              on ? 'bg-[var(--warm)]' : 'bg-[var(--bg-soft)]',
            )}
          >
            <span
              className={cn(
                'block h-5 w-5 rounded-full bg-[var(--bg-surface)] transition-transform',
                on ? 'translate-x-4' : 'translate-x-0',
              )}
            />
          </button>
        </div>
      )}

      <div
        className={cn(
          'border-b border-[var(--rule)] bg-[var(--bg-page)] px-5',
          compact ? 'py-4' : 'py-5',
        )}
      >
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full border border-[var(--rule)] bg-[var(--bg-surface)]',
              compact ? 'h-16 w-16' : 'h-20 w-20',
            )}
          >
            <span
              className={cn(
                'font-display font-semibold leading-none tabular-nums text-[var(--ink)]',
                compact ? 'text-[28px]' : 'text-[34px]',
              )}
            >
              {playing ? playing.note : '—'}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            {playing ? (
              <>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
                  Tonic · {playing.baseHz} Hz
                </div>
                <div className="mt-1 text-[12px] leading-snug text-[var(--ink-2)]">
                  Works for{' '}
                  <span className="font-semibold text-[var(--ink)]">{playing.note}</span>{' '}
                  {playing.modes.join(' · ')}
                </div>
                <div className="mt-2.5">
                  <DroneWave playing />
                </div>
              </>
            ) : (
              <>
                <div className="text-[12px] leading-snug text-[var(--ink-2)]">
                  Tap a key to start. Drone holds the tonal centre while you play over it.
                </div>
                <div className="mt-2.5 opacity-30">
                  <DroneWave playing={false} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className={cn('border-b border-[var(--rule)] px-5', compact ? 'py-4' : 'py-5')}
        role="group"
        aria-label="Drone key"
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
            All keys · 12
          </span>
          <span className="text-[10px] text-[var(--ink-3)]">● = trad-common</span>
        </div>
        <div className="grid grid-cols-6 gap-1.5">
          {DRONE_KEYS.map((k) => {
            const active = k.note === root;
            const accessibleName = k.enharmonic ? `${k.note} (${k.enharmonic})` : k.note;
            return (
              <button
                key={k.note}
                type="button"
                aria-pressed={active}
                aria-label={accessibleName}
                onClick={() => onRootChange(k.note)}
                className={cn(
                  'relative flex h-12 items-center justify-center rounded-[var(--r-sm)] font-display text-[14px] font-semibold tabular-nums transition-all',
                  active ? activeButton : idleButton,
                )}
              >
                <span>{k.note}</span>
                {k.enharmonic && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute right-1.5 top-1 font-mono text-[8px]',
                      active ? 'text-white/55' : 'text-[var(--ink-3)]',
                    )}
                  >
                    {k.enharmonic}
                  </span>
                )}
                {k.weight === 'primary' && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full',
                      active ? 'bg-white/55' : 'bg-[var(--ink-3)]/45',
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className={cn('border-b border-[var(--rule)] px-5', compact ? 'py-3.5' : 'py-4')}
        role="group"
        aria-label="Interval"
      >
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
          Interval
        </div>
        <div className="flex items-center gap-1">
          {DRONE_INTERVALS.map((iv) => {
            const active = iv.id === interval;
            return (
              <button
                key={iv.id}
                type="button"
                aria-pressed={active}
                aria-label={iv.label}
                onClick={() => onIntervalChange(iv.id)}
                className={cn(
                  'flex h-9 flex-1 flex-col items-center justify-center rounded-[var(--r-sm)] text-[11px] font-semibold',
                  active ? activeButton : idleButton,
                )}
              >
                <span className="leading-none">{iv.label}</span>
                <span
                  className={cn(
                    'mt-0.5 font-mono text-[9px] leading-none',
                    active ? 'text-white/60' : 'text-[var(--ink-3)]',
                  )}
                >
                  {iv.hint}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={cn('grid grid-cols-2 gap-4 px-5', compact ? 'py-3.5' : 'py-4')}>
        <div role="group" aria-label="Octave">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
            Octave
          </div>
          <div className="flex items-center gap-1">
            {DRONE_OCTAVES.map((o) => {
              const active = o === octave;
              return (
                <button
                  key={o}
                  type="button"
                  aria-pressed={active}
                  aria-label={o}
                  onClick={() => onOctaveChange(o)}
                  className={cn(
                    'h-8 flex-1 rounded-[var(--r-sm)] text-[11px] font-semibold capitalize',
                    active ? activeButton : idleButton,
                  )}
                >
                  {o}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
            <span>Volume</span>
            <span className="font-mono">{Math.round(volume * VOLUME_PERCENT)}</span>
          </div>
          <div className="flex h-8 items-center">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              aria-label="Volume"
              onChange={(e) => onVolumeChange(Number(e.target.value))}
              className="flex-1 accent-[var(--ink)]"
            />
          </div>
        </div>
      </div>

      {showFooter && (playing || onAutoMatch) && (
        <div className="flex items-center gap-2 border-t border-[var(--rule)] bg-[var(--bg-soft)] px-5 py-3">
          {onAutoMatch && (
            <button
              type="button"
              onClick={onAutoMatch}
              disabled={autoMatchDisabled}
              className={cn(
                'h-8 rounded-[var(--r-sm)] px-2.5 text-[11px] font-semibold',
                idleButton,
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
            >
              Auto-match tune key
            </button>
          )}
          {playing && (
            <button
              type="button"
              onClick={() => onOnChange(false)}
              className="ml-auto h-8 rounded-[var(--r-sm)] bg-[var(--ink)] px-3 text-[11px] font-semibold text-white hover:bg-[var(--ink)]/90"
            >
              Stop drone
            </button>
          )}
        </div>
      )}
    </div>
  );
}
